import { useParams, useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { neturalBlack, netural400, secondary500, errorRed, error50 } from "../../../constants/color";
import useAdminTransferDetail from "../../../Hooks/admin/useAdminTransferDetail";
import TransferStatusBadge from "../../../components/admin/marketplace/TransferStatusBadge";
import FinancialBreakdownCard from "../../../components/admin/marketplace/FinancialBreakdownCard";
import OrderInfoCard from "../../../components/admin/marketplace/OrderInfoCard";
import BusinessInfoCard from "../../../components/admin/marketplace/BusinessInfoCard";
import CustomerInfoCard from "../../../components/admin/marketplace/CustomerInfoCard";
import StatusTimeline from "../../../components/admin/marketplace/StatusTimeline";
import { formatId, formatDate } from "../../../utils/marketplaceHelpers";

const TransferDetailPage = () => {
  const { transferId } = useParams();
  const navigate = useNavigate();

  const {
    transfer,
    order,
    business,
    customer,
    statusHistory,
    stripeTransferUrl,
    stripeChargeUrl,
    loading,
    error,
  } = useAdminTransferDetail(transferId);

  const handleBackClick = () => {
    navigate("/admin/marketplace/transfers");
  };

  if (loading) {
    return (
      <div className="main-div-right-section">
        <div className="second-section-style">
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: netural400,
              fontSize: "16px",
            }}
          >
            Loading transfer details...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-div-right-section">
        <div className="second-section-style">
          <div
            style={{
              padding: "20px",
              backgroundColor: error50,
              borderRadius: "12px",
              color: errorRed,
              fontSize: "16px",
            }}
          >
            Error loading transfer details: {error.message}
          </div>
        </div>
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="main-div-right-section">
        <div className="second-section-style">
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: netural400,
              fontSize: "16px",
            }}
          >
            Transfer not found
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-div-right-section">
      <div className="second-section-style">
        {/* Back Button and Header */}
        <div style={{ marginBottom: "20px" }}>
          <button
            onClick={handleBackClick}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #DADADA",
              backgroundColor: "#ffffff",
              color: secondary500,
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
              marginBottom: "16px",
            }}
          >
            ← Back to Transfers
          </button>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: neturalBlack,
                  marginBottom: "8px",
                }}
              >
                Transfer {formatId(transfer._id)}
              </h2>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <TransferStatusBadge status={transfer.status} showTooltip={false} />
                {transfer.retryCount > 0 && (
                  <span style={{ color: netural400, fontSize: "14px" }}>
                    Retry: {transfer.retryCount}/3
                  </span>
                )}
                <span style={{ color: netural400, fontSize: "14px" }}>
                  Created: {formatDate(transfer.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <Row style={{ marginBottom: "20px" }}>
          <Col lg={6} md={12} style={{ marginBottom: "20px" }}>
            <FinancialBreakdownCard feeBreakdown={transfer.feeBreakdown} />
          </Col>
          <Col lg={6} md={12} style={{ marginBottom: "20px" }}>
            <OrderInfoCard order={order} stripeChargeUrl={stripeChargeUrl} />
          </Col>
        </Row>

        <Row style={{ marginBottom: "20px" }}>
          <Col lg={6} md={12} style={{ marginBottom: "20px" }}>
            <BusinessInfoCard business={business} />
          </Col>
          <Col lg={6} md={12} style={{ marginBottom: "20px" }}>
            <CustomerInfoCard customer={customer} />
          </Col>
        </Row>

        {/* Status Timeline */}
        <div style={{ marginBottom: "20px" }}>
          <StatusTimeline statusHistory={statusHistory} />
        </div>

        {/* Failure Info */}
        {(transfer.failureCode || transfer.failureReason) && (
          <div
            style={{
              padding: "16px",
              backgroundColor: error50,
              borderRadius: "12px",
              marginBottom: "20px",
              border: `1px solid ${errorRed}`,
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: errorRed,
                marginBottom: "8px",
              }}
            >
              Failure Information
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {transfer.failureCode && (
                <div style={{ fontSize: "14px" }}>
                  <span style={{ color: netural400 }}>Code: </span>
                  <span style={{ color: errorRed, fontFamily: "monospace" }}>
                    {transfer.failureCode}
                  </span>
                </div>
              )}
              {transfer.failureReason && (
                <div style={{ fontSize: "14px" }}>
                  <span style={{ color: netural400 }}>Reason: </span>
                  <span style={{ color: neturalBlack }}>
                    {transfer.failureReason}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reversal Info */}
        {transfer.reversalAmount > 0 && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#F3E5F5",
              borderRadius: "12px",
              marginBottom: "20px",
              border: "1px solid #9C27B0",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#9C27B0",
                marginBottom: "8px",
              }}
            >
              Reversal Information
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ fontSize: "14px" }}>
                <span style={{ color: netural400 }}>Amount: </span>
                <span style={{ color: neturalBlack, fontWeight: "500" }}>
                  ${transfer.reversalAmount.toFixed(2)}
                </span>
              </div>
              {transfer.reversalReason && (
                <div style={{ fontSize: "14px" }}>
                  <span style={{ color: netural400 }}>Reason: </span>
                  <span style={{ color: neturalBlack }}>
                    {transfer.reversalReason}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stripe Links */}
        {/* <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          {stripeTransferUrl && (
            <a
              href={stripeTransferUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 16px",
                borderRadius: "8px",
                border: "1px solid #DADADA",
                backgroundColor: "#ffffff",
                color: secondary500,
                fontSize: "14px",
                textDecoration: "none",
                fontWeight: "500",
              }}
            >
              View Transfer in Stripe →
            </a>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default TransferDetailPage;
